import { Suspense, useState, useEffect, useMemo, useRef } from 'react'
import { Canvas } from '@react-three/fiber'
import { OrbitControls, Environment, Center } from '@react-three/drei'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import * as THREE from 'three'

interface Model3DViewerProps {
  modelUrl: string | null
}

interface ModelWithProgressProps {
  url: string
  onProgress: (progress: number) => void
  onLoaded: () => void
  onError: (error: string) => void
}

function ModelWithProgress({ url, onProgress, onLoaded, onError }: ModelWithProgressProps) {
  const [gltf, setGltf] = useState<any>(null)
  const loaderRef = useRef<GLTFLoader | null>(null)

  useEffect(() => {
    if (!url) return

    const loader = new GLTFLoader()
    loaderRef.current = loader

    loader.load(
      url,
      (loadedGltf) => {
        setGltf(loadedGltf)
        onProgress(100)
        onLoaded()
      },
      (xhr) => {
        if (xhr.lengthComputable) {
          const progress = Math.round((xhr.loaded / xhr.total) * 100)
          onProgress(progress)
        }
      },
      (error) => {
        console.error('Model loading error:', error)
        onError('模型加载失败')
      }
    )

    return () => {
      loaderRef.current = null
    }
  }, [url, onProgress, onLoaded, onError])

  const clonedScene = useMemo(() => {
    if (!gltf) return null
    
    const clone = gltf.scene.clone()
    
    // Ensure materials are properly set up
    clone.traverse((child: any) => {
      if (child instanceof THREE.Mesh) {
        if (child.material) {
          child.material.needsUpdate = true
        }
      }
    })
    
    return clone
  }, [gltf])

  if (!clonedScene) return null

  return (
    <Center>
      <primitive object={clonedScene} />
    </Center>
  )
}

function LoadingSpinner() {
  return (
    <mesh rotation={[0, 0, 0]}>
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial color="#d4af37" wireframe />
    </mesh>
  )
}

export default function Model3DViewer({ modelUrl }: Model3DViewerProps) {
  const [key, setKey] = useState(0)
  const [progress, setProgress] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset state when modelUrl changes
  useEffect(() => {
    if (modelUrl) {
      setKey(prev => prev + 1)
      setProgress(0)
      setIsLoading(true)
      setError(null)
    }
  }, [modelUrl])

  if (!modelUrl) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-luxury-navy-800">
        <div className="text-center">
          <div className="w-24 h-24 border-4 border-luxury-gold-800/30 border-dashed rounded-full mx-auto mb-4 flex items-center justify-center">
            <span className="text-4xl text-gray-500">3D</span>
          </div>
          <p className="text-gray-500">生成的3D模型将在此处显示</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-luxury-navy-800">
        <div className="text-center">
          <div className="w-24 h-24 border-4 border-red-500/30 border-dashed rounded-full mx-auto mb-4 flex items-center justify-center">
            <span className="text-4xl text-red-500">!</span>
          </div>
          <p className="text-red-400 mb-2">{error}</p>
          <button
            onClick={() => {
              setError(null)
              setProgress(0)
              setIsLoading(true)
              setKey(prev => prev + 1)
            }}
            className="px-4 py-2 bg-luxury-gold-600 text-white rounded hover:bg-luxury-gold-500"
          >
            重试
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="relative w-full h-full">
      {/* Progress Bar Overlay */}
      {isLoading && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-luxury-navy-800/90 backdrop-blur-sm">
          <div className="text-center">
            {/* Spinning loader */}
            <div className="w-16 h-16 border-4 border-luxury-gold-800/30 border-t-luxury-gold-500 rounded-full animate-spin mx-auto mb-4" />
            
            {/* Progress percentage */}
            <p className="text-2xl font-bold text-luxury-gold-500 mb-2">{progress}%</p>
            <p className="text-gray-400 mb-4">正在加载模型...</p>
            
            {/* Progress bar */}
            <div className="w-64 h-2 bg-luxury-navy-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-luxury-gold-600 to-luxury-gold-400 transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      <Canvas
        key={key}
        camera={{ position: [0, 0, 5], fov: 50 }}
        gl={{
          antialias: true,
          alpha: true,
        }}
        onCreated={({ gl }) => {
          gl.setClearColor(new THREE.Color('#0a1628'), 1)
        }}
      >
        <ambientLight intensity={0.8} />
        <directionalLight position={[5, 5, 5]} intensity={1.2} />
        <directionalLight position={[-5, 3, 5]} intensity={0.6} />
        <pointLight position={[0, 5, 0]} intensity={0.5} />
        
        <Suspense fallback={<LoadingSpinner />}>
          <ModelWithProgress 
            url={modelUrl} 
            onProgress={setProgress}
            onLoaded={() => setIsLoading(false)}
            onError={(err) => {
              setError(err)
              setIsLoading(false)
            }}
          />
          <Environment files="/hdri/studio_small_03_1k.hdr" />
        </Suspense>
        
        <OrbitControls
          enablePan={true}
          enableZoom={true}
          enableRotate={true}
          autoRotate={true}
          autoRotateSpeed={1}
        />
      </Canvas>
    </div>
  )
}
